import React, {useEffect, useMemo, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useSelector} from 'react-redux';
import {RootState, useAppDispatch} from '../../../store';
import {
  deleteInferenceApp,
  fetchInferenceAppsSync,
} from '../../../store/prodDeployments/dashboard';
import {CopyableURL} from '../CopyableURL/CopyableURL';
import {Metadata, MetadataList} from '../MetadataList/MetadataList';
import './InferenceAppDetails.scss';
import {Link} from 'react-router-dom';
import {InferenceApplicationsRequest} from '../../../types/deployment/InferenceApplicationsRequest';
import {SinglePageApplication} from '../../../types/project/Application';
import {StudioTooltip} from '../../../base-components/StudioTooltip';
import {Delete, Edit} from '@material-ui/icons';
import {ConfirmDialog} from '../../../base-components/StudioConfirmDialog';
import {toast} from '../../../base-components/StudioToast';

type InferenceAppDetailsProps = {
  spa: SinglePageApplication;
  showLinkToEdit: boolean;
  showActionButtons: boolean;
};

export const InferenceAppDetails = ({
  spa,
  showLinkToEdit,
  showActionButtons,
}: InferenceAppDetailsProps) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const groupId = useSelector((state: RootState) => state.user.groupId);
  const [batchInferenceApiUrl, setBatchInferenceApiUrl] = useState<string>();
  const [batchInferenceSpaUrl, setBatchInferenceSpaUrl] = useState<string>();
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
  }>({isOpen: false});

  const applicationId = spa.applicationId;
  const projectId = spa.projectId;
  const deploymentId = spa.target.deploymentId;

  useEffect(() => {
    async function fetchInferenceAppDetails() {
      if (spa.id && groupId) {
        const requestBody: InferenceApplicationsRequest = {
          firstPage: 0,
          pageSize: 100,
          paginated: false,
          orderDefinitions: [],
          params: {
            id: spa.id,
          },
          groupId,
        };

        const response = await fetchInferenceAppsSync(requestBody);
        if (response && response.count > 0) {
          const app = response.entries.find(app => app.id === spa.id);
          if (app) {
            setBatchInferenceApiUrl(app?.batchInferenceUrl);
            setBatchInferenceSpaUrl(app?.inferencePageUrl);
          }
        }
      }
    }
    fetchInferenceAppDetails();
  }, [projectId, dispatch, groupId, spa.id]);

  const editLink = useMemo(
    () =>
      spa.target.type === 'PRODUCTION'
        ? `/project/${projectId}/inference/${applicationId}/deployment/${deploymentId}/configure?spaId=${spa.id}`
        : `/project/${projectId}/inference/${applicationId}/create?spaId=${spa.id}&viewOnly=true`,
    [spa, projectId, applicationId, deploymentId]
  );

  return (
    <>
      <MetadataList>
        <div className="inference-app-details__name__container">
          <div>
            <Metadata label="prodDeployment.name">
              <div>{spa.name}</div>
            </Metadata>
          </div>
          {showActionButtons && (
            <div className="inference-app-details__name__container__icon">
              {showLinkToEdit && (
                <>
                  <Link to={editLink}>
                    <StudioTooltip
                      title={intl.formatMessage({
                        id: 'prodDeployment.inferenceApp.tooltip',
                      })}
                      placement="top"
                    >
                      <Edit className="inference-app-details__name__container__icon_edit" />
                    </StudioTooltip>
                  </Link>
                </>
              )}
              <StudioTooltip
                title={intl.formatMessage({
                  id: 'form.delete',
                })}
                placement="top"
              >
                <Delete
                  className="inference-app-details__name__container__icon_delete"
                  onClick={() => setDeleteConfirmation({isOpen: true})}
                />
              </StudioTooltip>
            </div>
          )}
        </div>
        <Metadata label="prodDeployment.inputType">
          <FormattedMessage
            id={spa.input.type === 'BATCH' ? 'inferenceApp.batch' : 'inferenceApp.stream'}
          />
        </Metadata>
        {spa.input.type === 'BATCH' && (
          <Metadata label="prodDeployment.api">
            {batchInferenceApiUrl ? (
              <CopyableURL path={batchInferenceApiUrl} />
            ) : (
              <div>API Not Published</div>
            )}
          </Metadata>
        )}
        <Metadata label="prodDeployment.singlePage.full">
          {batchInferenceSpaUrl ? (
            <CopyableURL path={batchInferenceSpaUrl} />
          ) : (
            <div>SPA Not Published</div>
          )}
        </Metadata>
        <Metadata label="prodDeployment.type">
          <div>{deploymentId ? 'Production' : 'Test'}</div>
        </Metadata>
      </MetadataList>
      <ConfirmDialog
        type="confirm"
        title={intl.formatMessage({id: 'prodDeployment.deleteAppConfirmTitle'})}
        message={intl.formatMessage({id: 'prodDeployment.deleteAppConfirmMessage'})}
        submitLabel={intl.formatMessage({
          id: 'prodDeployment.deleteConfirmSubmitLabel',
        })}
        open={deleteConfirmation.isOpen}
        onClose={() => setDeleteConfirmation({isOpen: false})}
        onOk={async () => {
          await dispatch(deleteInferenceApp({applicationId, spaId: spa?.id}));
          setDeleteConfirmation({isOpen: false});
          toast.success(`Successfully deleted application`);
        }}
      />
    </>
  );
};
